import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { usePlantingsList } from "@/modules/plantings/hooks/usePlantings";
import type { Planting } from "@/modules/plantings/types";
import { formatDateTime, geometryTypeLabel } from "@/modules/plantings/utils/format";
import { EmptyState } from "@/shared/components/EmptyState";
import { useUrlTableState } from "@/shared/hooks/useUrlState";
import { formatTableIndex } from "@/shared/utils/formatTableIndex";
import { tablePagination } from "@/shared/utils/tablePagination";

interface TerritoryPlantingsTableProps {
	organizationId: string;
	territoryId: string;
}

/** Read-only list of plantings located in the territory */
export function TerritoryPlantingsTable({
	organizationId,
	territoryId,
}: TerritoryPlantingsTableProps) {
	// Pagination lives in the URL (prefixed to avoid collisions with sibling tables)
	const { page, limit, setPage } = useUrlTableState({ prefix: "tplant" });

	const { data, isLoading } = usePlantingsList({ organizationId, territoryId, page, limit });
	const items = data?.data?.items ?? [];
	const total = data?.data?.meta?.total ?? 0;

	const columns: ColumnsType<Planting> = [
		{
			title: "№",
			key: "index",
			width: 55,
			align: "center",
			render: (_: unknown, __: unknown, index: number) => formatTableIndex(page, limit, index),
		},
		{
			title: "O'simlik",
			key: "plantName",
			ellipsis: true,
			render: (_: unknown, record: Planting) => record.plantName ?? record.plantId,
		},
		{
			title: "Shartnoma",
			key: "contract",
			width: 150,
			render: (_: unknown, record: Planting) => record.contractNumber ?? "—",
		},
		{
			title: "Soni",
			dataIndex: "quantity",
			key: "quantity",
			width: 100,
			render: (quantity: number | null) => quantity?.toLocaleString("uz-UZ") ?? "—",
		},
		{
			title: "Geometriya",
			key: "geometry",
			width: 110,
			render: (_: unknown, record: Planting) =>
				record.geometry ? geometryTypeLabel(record.geometry.type) : "—",
		},
		{
			title: "Ekilgan sana",
			dataIndex: "plantedAt",
			key: "plantedAt",
			width: 150,
			render: (date: string) => formatDateTime(date),
		},
	];

	return (
		<Table
			columns={columns}
			dataSource={items}
			loading={isLoading}
			rowKey="id"
			size="middle"
			pagination={tablePagination({
				page,
				limit,
				total,
				onChange: setPage,
				sizeChanger: false,
			})}
			locale={{
				emptyText: <EmptyState description="Bu hududda ekinlar ro'yxatga olinmagan" />,
			}}
		/>
	);
}
